import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { sprekersApi } from '../api';
import { MediaThumbnail, VisualSlideThumbnail } from './TemplatePreview';
import type { PresentationTemplate, PresentationTemplateSlide } from '../types';

interface Props {
  template: PresentationTemplate | null;
  onOpenChange: (open: boolean) => void;
}

/** "Bekijken": alle slides van een slides-sjabloon als miniaturen; klik op een miniatuur voor een groot voorbeeld. */
export default function TemplateSlidesDialog({ template, onOpenChange }: Props) {
  const [slides, setSlides] = useState<PresentationTemplateSlide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!template) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    setSlides([]);
    setSelectedId(null);
    sprekersApi
      .getTemplateSlides(template.id)
      .then((data) => {
        if (active) setSlides(data);
      })
      .catch(() => {
        if (active) setError('Kon de slides van dit sjabloon niet laden.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [template]);

  const selected = slides.find((slide) => slide.id === selectedId) || slides[0] || null;

  return (
    <Dialog open={template !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{template?.name}</DialogTitle>
          <DialogDescription>
            {template?.template_type === 'editable'
              ? 'Bewerkbaar sjabloon: na gebruiken pas je tekst, afbeeldingen en vormen aan in Zoea Studio.'
              : 'Visueel sjabloon: de opmaak blijft behouden, maar tekstvakken en vormen zijn niet los te bewerken.'}
          </DialogDescription>
        </DialogHeader>
        {loading && <p className="text-sm text-slate-500">Slides laden...</p>}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {!loading && !error && slides.length === 0 && <p className="text-sm text-slate-500">Dit sjabloon bevat geen slides.</p>}
        {selected && (
          <div className="space-y-3">
            {template?.template_type === 'editable' ? (
              <VisualSlideThumbnail visual={selected} className="w-full rounded-lg border border-slate-200" />
            ) : (
              <MediaThumbnail
                mediaId={selected.media_id} alt={`Slide ${selected.position + 1} van ${template?.name}`}
                className="w-full rounded-lg border border-slate-200"
              />
            )}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {slides.map((slide) => (
                <button
                  key={slide.id} type="button" onClick={() => setSelectedId(slide.id)}
                  aria-label={`Slide ${slide.position + 1}`} aria-pressed={slide.id === selected.id}
                  className={`overflow-hidden rounded-md border-2 text-left ${slide.id === selected.id ? 'border-teal-500' : 'border-transparent'}`}
                >
                  {template?.template_type === 'editable'
                    ? <VisualSlideThumbnail visual={slide} />
                    : <MediaThumbnail mediaId={slide.media_id} alt={`Miniatuur slide ${slide.position + 1}`} />}
                  <span className="block px-1 py-0.5 text-xs text-slate-500">{slide.position + 1}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
